// C-1: ONE search index over every Control & Admin page and tab. Built from MODULE_TREE (so a new page/tab is in the index by construction)
// + KEYWORDS (EN | FR words per page/tab; the guard test fails when a tree page/tab has no row) + the in-page cards other pages register
// (docMgmtSearchEntries today). Same entry shape as the Settings search: { tabId, tabLabel, id, searchText, displayPrimary, displayContext }.
import { MODULE_TREE } from '../../config/moduleTree';
import { docMgmtSearchEntries, foldText, DOC_TAB_IDS } from './docMgmtSearch';

export { foldText };
const mod = () => MODULE_TREE.find(g => g.admin).modules[0];
// Engines / Supporting Engines tabs are raw support views: only the page is indexed, not its 45 tabs.
export const PAGE_ONLY = new Set(['engines', 'supporting-engines']);

// key = `<section>` or `<section>/<tab>`; value = "EN words | FR words" (search only, never displayed).
export const KEYWORDS = {
  'organisation-settings': 'organisation organization master data | organisation donnees de base',
  'organisation-settings/bands': 'flocks batches | lots bandes',
  'organisation-settings/farms-dash': 'farms capacity phase decommission | fermes capacite phase desactiver',
  'organisation-settings/sites': 'sites locations | sites emplacements',
  'organisation-settings/storage': 'storage warehouse stores | stockage entrepot magasins',
  'organisation-settings/partners': 'customers suppliers credit limit delivery | clients fournisseurs partenaires limite de credit livraison',
  'organisation-settings/people': 'people staff employees | personnes personnel employes',
  'organisation-settings/breeds': 'breeds genetics | races genetique',
  'finance-settings': 'finance accounting setup | finance comptabilite configuration',
  'finance-settings/accounts': 'accounts chart bank cash | comptes plan comptable banque caisse',
  'finance-settings/expense-categories': 'expense categories subtypes | categories de depenses sous-types',
  'finance-settings/expense-groups': 'expense groups | groupes de depenses',
  'finance-settings/revenue-categories': 'other revenue categories | categories de revenus divers autres revenus',
  'finance-settings/projects': 'projects cost centres | projets centres de couts',
  'finance-settings/period-lock': 'period lock close month closing date | verrouillage de periode cloture mois date de cloture',
  'finance-settings/return-reasons': 'sale return reasons refund | motifs de retour de vente remboursement',
  'documents-management': 'documents print invoice template | documents impression facture modele',
  'documents-management/doc-general': 'company header footer paper | societe en-tete pied de page papier',
  'documents-management/numbering': 'numbering invoice number counter reset period series credit note | numerotation numero de facture compteur remise a zero serie avoir',
  'documents-management/print-documents': 'print columns terms fields | impression colonnes conditions champs',
  'documents-management/credit-notes-refunds': 'credit note avoir refund | avoir remboursement note de credit',
  'inventory-settings': 'inventory items master data | stock articles donnees de base',
  'inventory-settings/items': 'items products units price | articles produits unites prix',
  'inventory-settings/item-categories': 'item categories | categories d articles',
  'operations-settings': 'operations setup | operations configuration',
  'operations-settings/activities': 'activities tasks | activites taches',
  'operations-settings/operation-types': 'operation types | types d operations',
  'operations-settings/mortality-causes': 'mortality causes death | causes de mortalite deces',
  'system-settings': 'system settings | parametres systeme',
  'system-settings/general': 'general inventory tie-out a4 thresholds | general ecart inventaire seuils',
  'system-settings/benchmarks': 'thresholds benchmarks targets | seuils references objectifs',
  'system-settings/sanity-rules': 'sanity rules tolerance a4 | regles de coherence tolerance',
  'system-settings/formatting': 'formatting decimals date number currency | formatage decimales date nombre devise',
  'system-settings/category-capabilities': 'category capabilities operations permitted | capacites des categories operations autorisees',
  'access-control': 'access control roles users permissions | controle d acces roles utilisateurs permissions',
  'access-control/roles': 'roles pages permissions | roles pages permissions droits',
  'access-control/users': 'users accounts invite password | utilisateurs comptes inviter mot de passe',
  'data-refreshes': 'data refresh jobs schedule history | actualisation des donnees taches planification historique',
  'data-health': 'data health rules plausibility warnings | qualite des donnees regles plausibilite avertissements',
  'engines': 'engines raw views | moteurs vues brutes',
  'supporting-engines': 'supporting engines views | moteurs de support vues',
  'sanity': 'sanity report integrity | rapport de coherence integrite',
  'audit-log': 'audit log changes history | journal d audit modifications historique',
  'lifecycle-log': 'lifecycle log events | journal du cycle de vie evenements',
  'language': 'language translations terms | langue traductions termes',
  'language/terms': 'terms labels translation french english | termes libelles traduction francais anglais',
  'language/engine': 'engine messages translation | messages du moteur traduction',
  'data-entry-legacy': 'data entry legacy | saisie ancienne',
  'changelog': 'changelog release notes version | journal des modifications notes de version',
};

// ctx = { nav: useNavLabels(), isAdmin, isVisible, isTabVisible }; nextId() -> unique numeric id.
export function controlAdminEntries(ctx, nextId) {
  const m = mod(), { nav, isAdmin, isVisible, isTabVisible } = ctx;
  const open = (s, x) => isAdmin || (!s.roles && !(x && x.roles) && isVisible(s.page) && (s.page !== 'settings' || !x || isTabVisible('settings', x.tab)));
  const out = [];
  const push = (s, x) => {
    const key = x ? `${s.key}/${x.key}` : s.key, sec = nav.sec(m, s), label = x ? nav.tab(m, s, x) : sec;
    out.push({
      id: `sr-${nextId()}`, nav: true, path: `/${m.key}/${s.key}${x && !PAGE_ONLY.has(s.key) ? `/${x.key}` : ''}`,
      tabId: x && s.page === 'settings' ? x.tab : `nav:${s.key}`, tabLabel: sec,
      searchText: foldText([label, x ? x.label : s.label, sec, KEYWORDS[key], x ? x.blurb : s.blurb].filter(Boolean).join(' ')),
      displayPrimary: label, displayContext: x ? sec : String(s.blurb || '').slice(0, 90),
    });
  };
  for (const s of m.sections) {
    if (s.hidden || (!open(s) && !s.tabs)) continue;
    if (open(s)) push(s);
    if (!PAGE_ONLY.has(s.key)) for (const x of s.tabs || []) if (open(s, x) && !(s.page === 'settings' && DOC_TAB_IDS.has(x.tab))) push(s, x);
  }
  return out;
}

// Registry of in-page cards: each page contributes its rows here (docMgmtSearch.js for Documents management).
export function allControlAdminEntries(ctx, nextId) {
  const cards = ctx.isAdmin ? docMgmtSearchEntries(nextId).map(e => ({ ...e, nav: true, path: `/${mod().key}/documents-management/${e.tabId}` })) : [];
  return [...controlAdminEntries(ctx, nextId), ...cards];
}

// A pick on another page is highlighted once its Settings tab has mounted.
let pending = null;
export const setPendingHit = e => { pending = e; };
export const takePendingHit = () => { const e = pending; pending = null; return e; };
